import { useState, useRef, useEffect } from "react"
import { Loader2, RotateCcw, Info } from "lucide-react"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { toast } from "sonner"
import { processVirtualStaging, uploadAssetFromUrl, uploadFile, getUserCredits, type CreditDetails } from "@/lib/api-service"
import { getImageUrl } from "@/lib/image-utils"
import { ReactCompareSlider, ReactCompareSliderImage } from "react-compare-slider"
import { ErrorMessage } from "./error-message"
export type RoomType = "Living" | "Bed" | "Kitchen" | "Dining" | "Bathroom" | "Home Office" | "Outdoor" | "Kids Room"

export type FurnitureStyle =
  | "Modern"
  | "Scandinavian"
  | "Industrial"
  | "Mid-Century"
  | "Luxury"
  | "Farmhouse"
  | "Coastal"
  | "Standard"

// Map frontend room types and styles to backend expected values if needed
export const roomTypeMapping: Record<RoomType, string> = {
  "Living": "living",
  "Bed": "bed",
  "Kitchen": "kitchen",
  "Dining": "dining",
  "Bathroom": "bathroom",
  "Home Office": "home_office",
  "Outdoor": "outdoor",
  "Kids Room": "kids_room"
}

export const furnitureStyleMapping: Record<FurnitureStyle, string> = {
  "Modern": "modern",
  "Scandinavian": "scandinavian",
  "Industrial": "industrial",
  "Mid-Century": "midcentury",
  "Luxury": "luxury",
  "Farmhouse": "farmhouse",
  "Coastal": "coastal",
  "Standard": "standard"
}

export type WidgetState = "upload" | "configure" | "result"

interface VirtualStagingWidgetProps {
  user_id?: string;
  client_key?: string;
  user_image_url?: string;
  room_type?: string;
  style?: string;
  auto_declutter?: boolean;
  extra?: Record<string, any>;
}

export default function VirtualStagingWidget({
  user_id,
  client_key,
  user_image_url,
  room_type,
  style,
  auto_declutter = true,
  extra = {}
}: VirtualStagingWidgetProps) {
  // Check for mandatory parameters
  const missingParams: string[] = [];
  if (!user_id) missingParams.push('user_id');
  if (!user_image_url) missingParams.push('user_image_url');
  if (!client_key) missingParams.push('client_key');

  // If any required parameters are missing, show error message
  if (missingParams.length > 0) {
    return <ErrorMessage missingParams={missingParams} />;
  }
  // Determine initial room type based on props or default to "Living"
  const initialRoomType = (): RoomType => {
    if (!room_type) return "Living";

    // Find the matching room type
    const entries = Object.entries(roomTypeMapping) as [RoomType, string][];
    const match = entries.find(([_, value]) => value === room_type);
    return match ? match[0] : "Living";
  };

  // Determine initial style based on props or default to "Modern"
  const initialStyle = (): FurnitureStyle => {
    if (!style) return "Modern";

    // Find the matching style
    const entries = Object.entries(furnitureStyleMapping) as [FurnitureStyle, string][];
    const match = entries.find(([_, value]) => value === style);
    return match ? match[0] : "Modern";
  };

  // Component state
  const [selectedRoomType, setSelectedRoomType] = useState<RoomType>(initialRoomType())
  const [selectedStyle, setSelectedStyle] = useState<FurnitureStyle>(initialStyle())
  const [useAutoDeclutter, setUseAutoDeclutter] = useState(auto_declutter)
  const [isProcessing, setIsProcessing] = useState(false)
  const [processingMessage, setProcessingMessage] = useState("Loading...")
  const [creditDetails, setCreditDetails] = useState<CreditDetails | null>(null)

  // Store all generated images in a ref to ensure we don't lose them due to closure issues
  const generatedImagesRef = useRef<string[]>([])
  const [generatedImages, setGeneratedImages] = useState<string[]>([])
  const [selectedImageIndex, setSelectedImageIndex] = useState(0)
  const [widgetState, setWidgetState] = useState<WidgetState>(user_image_url ? "configure" : "upload")
  const [originalImage, setOriginalImage] = useState(user_image_url || "") // Backend image URL
  const [originalImagePreview, setOriginalImagePreview] = useState(user_image_url || "") // Local preview URL
  const [processedImage, setProcessedImage] = useState("")
  const [uploadedAssetId, setUploadedAssetId] = useState<string | null>(null)

  // Fetch user credits when widget initializes
  useEffect(() => {
    const fetchCredits = async () => {
      if (user_id && client_key) {
        try {
          const credits = await getUserCredits(client_key, { userId: user_id });
          setCreditDetails(credits);
        } catch (error) {
          console.error("Failed to fetch user credits:", error);

          // Set default credits for testing
          setCreditDetails({
            userId: user_id,
            designsLeft: 3,
            totalDesigns: 3,
            canDesign: true
          });
        }
      } else {
        // Set default credits for testing
        setCreditDetails({
          userId: user_id || 'anonymous',
          designsLeft: 3,
          totalDesigns: 3,
          canDesign: true
        });
      }
    };

    fetchCredits();
  }, [user_id, client_key]);

  // Pre-upload the image from URL if provided
  useEffect(() => {
    const preUploadImage = async () => {
      if (user_image_url && widgetState === "configure") {
        try {
          setIsProcessing(true);
          setProcessingMessage("Preparing your image...");

          const asset = await uploadAssetFromUrl(user_image_url, client_key);
          setUploadedAssetId(asset.id);
          setOriginalImagePreview(getImageUrl(asset?.asset_id, 'original'));

        } catch (error) {
          console.error("Failed to pre-upload image:", error);
          toast.error("Failed to prepare image. Please try again.");
        } finally {
          setIsProcessing(false);
        }
      }
    };

    preUploadImage();
  }, [user_image_url, widgetState]);

  // Handle image upload completion
  const handleImageUploaded = (imageUrl: string, assetId: string, previewUrl?: string) => {
    setOriginalImage(imageUrl)
    setOriginalImagePreview(previewUrl || imageUrl)
    setUploadedAssetId(assetId)
    setWidgetState("configure")
  }

  // Handle applying virtual staging
  const handleApplyAndStage = async () => {
    if (!originalImage || !uploadedAssetId) {
      toast.error("Please upload an image first.");
      return;
    }

    // Check if user has credits left
    if (creditDetails && creditDetails.designsLeft === 0) {
      // No credits left, sending buy_more_credits message
      window.VirtualStagingWidget.sendMessage({
        type: 'buy_more_credits',
        data: {
          userId: creditDetails.userId
        }
      });
      toast.error("You don't have any designs left. Please purchase more credits.");
      return;
    }

    setIsProcessing(true)
    setProcessingMessage("Loading...")

    try {
      const response = await processVirtualStaging(
        uploadedAssetId,
        originalImage,
        selectedRoomType,
        selectedStyle,
        useAutoDeclutter,
        user_id,
        extra,
        client_key
      );

      // Set the processed image from the API response
      setProcessedImage(response.result_image_url)

      // Add to generated images gallery and also store in ref
      const newImages = [response.result_image_url, ...generatedImagesRef.current]
      setGeneratedImages(newImages)
      generatedImagesRef.current = newImages

      // Ensure the first image is selected
      setSelectedImageIndex(0)

      // Reduce credits after successful processing
      if (creditDetails) {
        setCreditDetails({
          ...creditDetails,
          designsLeft: Math.max(0, creditDetails.designsLeft - 1),
          canDesign: creditDetails.designsLeft > 1
        });
      }

      // Change widget state to show results
      setWidgetState("result")

      // Notify the parent that a design was generated
      window.VirtualStagingWidget.sendMessage({
        type: 'design_generated',
        data: {
          designsLeft: creditDetails?.designsLeft || 0,
          canDesign: creditDetails?.canDesign || false
        }
      });

      toast.success("Virtual staging complete!");
    } catch (error) {
      console.error('Error processing image:', error)
      toast.error('Failed to process image. Please try again.')
    } finally {
      setIsProcessing(false)
    }
  }

  // Handle thumbnail selection
  const handleSelectImage = (image: string, index: number) => {
    setProcessedImage(image)
    setSelectedImageIndex(index)
  }

  // Handle reset to upload new image
  const handleReset = () => {
    setWidgetState("upload")
    setOriginalImage("")
    setOriginalImagePreview("")
    setProcessedImage("")
    setGeneratedImages([])
    generatedImagesRef.current = []
    setSelectedImageIndex(0)
    setUploadedAssetId(null)
  }

  // Handle buying more credits
  const handleBuyMoreCredits = () => {
    // Send message to parent window
    window.VirtualStagingWidget.sendMessage({
      type: 'buy_more_credits',
      data: {
        userId: creditDetails?.userId || user_id || 'anonymous'
      }
    });
  }

  // Listen for credits updated message from parent window
  useEffect(() => {
    const handleCreditsUpdated = (event: MessageEvent) => {
      if (event.data && event.data.type === 'credits_updated') {
        // Received credits_updated message
        // Refresh credit details
        if (user_id && client_key) {
          getUserCredits(client_key, { userId: user_id })
            .then(credits => {
              setCreditDetails(credits);
            })
            .catch(error => {
              console.error("Failed to fetch updated credits:", error);
            });
        }
      }
    };

    window.addEventListener('message', handleCreditsUpdated);

    return () => {
      window.removeEventListener('message', handleCreditsUpdated);
    };
  }, [user_id, client_key]);

  // Placeholder component that will be replaced later
  const ImageUploader = ({ onImageUploaded }: { onImageUploaded: (imageUrl: string, assetId: string, previewUrl?: string) => void }) => {
    return (
      <div className="h-full flex flex-col items-center justify-center bg-gray-100 p-8 rounded-lg">
        <div className="text-center mb-6">
          <h2 className="text-xl font-bold mb-2">Upload Room Image</h2>
          <p className="text-gray-600">Upload a photo of the room you want to stage</p>
        </div>
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 w-full max-w-md flex flex-col items-center justify-center hover:border-dzinly-primary transition-colors">
          <p className="text-gray-500 mb-4 text-center">Drag & drop your image here, or click to browse</p>
          <Button variant="dzinly">Select Image</Button>
        </div>
      </div>
    );
  };

  // Placeholder components that will be replaced later
  const UploadedImagePreview = ({ imageUrl }: { imageUrl: string }) => {
    return (
      <div className="h-full flex items-center justify-center bg-gray-100 p-4">
        <img src={imageUrl} alt="Room to stage" className="max-h-full max-w-full object-contain" />
      </div>
    );
  };

  const ImageComparison = ({ beforeImage, afterImage }: { beforeImage: string, afterImage: string }) => {
    return (
      <div className="h-full flex flex-col md:flex-row">

        <ReactCompareSlider
          itemOne={
            <ReactCompareSliderImage
              src={beforeImage}
              alt="Original Design"
              className="object-contain"
              style={{ maxHeight: "500px", maxWidth: "100%" }}
            />
          }
          itemTwo={
            <ReactCompareSliderImage
              src={afterImage}
              alt="Generated Design"
              className="object-contain"
              style={{ maxHeight: "500px", maxWidth: "100%" }}
              onError={(e) => {
                console.error("Failed to load generated image");
                e.currentTarget.src = "/placeholder.svg?height=500&width=500";
              }}
            />
          }
          position={50}
          className="max-h-[500px]"
          style={{
            maxHeight: '500px',
            width: 'auto'
          }}
        />
      </div>

    );
  };

  const RoomTypeSelector = ({ selectedRoomType, onSelectRoomType }: {
    selectedRoomType: RoomType;
    onSelectRoomType: (roomType: RoomType) => void
  }) => {
    const roomTypes: RoomType[] = [
      "Living", "Bed", "Kitchen", "Dining", "Bathroom", "Home Office", "Outdoor", "Kids Room"
    ];

    return (
      <div>
        <h2 className="text-xl font-bold mb-1">Room Type</h2>
        <p className="text-gray-500 mb-4 text-md">Select the room type you want to stage.</p>
        <div className="grid grid-cols-2 gap-3">
          {roomTypes.map((roomType) => (
            <div
              key={roomType}
              className={`cursor-pointer rounded-lg py-3 px-2 transition-all ${selectedRoomType === roomType
                  ? 'bg-blue-100 border-blue-500 border'
                  : 'bg-gray-100 hover:bg-gray-200'
                }`}
              onClick={() => onSelectRoomType(roomType)}
            >
              <div className="text-center">
                <span className="text-base font-medium">{roomType}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const FurnitureStyleSelector = ({ selectedStyle, onSelectStyle }: {
    selectedStyle: FurnitureStyle;
    onSelectStyle: (style: FurnitureStyle) => void;
  }) => {
    // Import the furniture style presets - this will be used in actual implementation
    // Import will be added at the top of the file
    const styleData = [
      {
        style: "Modern",
        image: "https://images.renovateai.app/ren/resize:fit:1366:768:0/watermark:0:soea:0:0:0.3/plain/s3://renovate-ai/001Modern.png",
      },
      {
        style: "Scandinavian",
        image: "https://images.renovateai.app/ren/resize:fit:1366:768:0/watermark:0:soea:0:0:0.3/plain/s3://renovate-ai/017Scand.png",
      },
      {
        style: "Industrial",
        image: "https://images.renovateai.app/ren/resize:fit:1366:768:0/watermark:0:soea:0:0:0.3/plain/s3://renovate-ai/012Industrial.png",
      },
      {
        style: "Mid-Century",
        image: "https://images.renovateai.app/ren/resize:fit:1366:768:0/watermark:0:soea:0:0:0.3/plain/s3://renovate-ai/025Midcenturymodern.png",
      },
      {
        style: "Luxury",
        image: "https://images.renovateai.app/ren/resize:fit:1366:768:0/watermark:0:soea:0:0:0.3/plain/s3://renovate-ai/037IndustrialLuxe.png",
      },
      {
        style: "Farmhouse",
        image: "https://images.renovateai.app/ren/resize:fit:1366:768:0/watermark:0:soea:0:0:0.3/plain/s3://renovate-ai/034Farmhouse.png",
      },
      {
        style: "Coastal",
        image: "https://images.renovateai.app/ren/resize:fit:1366:768:0/watermark:0:soea:0:0:0.3/plain/s3://renovate-ai/010Coastal.png",
      },
      {
        style: "Standard",
        image: "https://images.renovateai.app/ren/resize:fit:1366:768:0/watermark:0:soea:0:0:0.3/plain/s3://renovate-ai/020Traditional.png",
      },
    ];

    return (
      <div>
        <h2 className="text-xl font-bold mb-4">Furniture Style</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {styleData.map((item) => {
            const style = item.style as FurnitureStyle;
            return (
              <div
                key={style}
                className={`cursor-pointer rounded-lg overflow-hidden transition-all ${selectedStyle === style ? 'border-blue-500 border-2' : 'border-transparent hover:opacity-90'
                  }`}
                onClick={() => onSelectStyle(style)}
              >
                <div className="relative aspect-square w-full">
                  <img
                    src={item.image}
                    alt={style}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-30 flex items-end justify-center pb-1 ">
                    <span className="text-white text-xs font-medium text-center">{style}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const ThumbnailGallery = ({
    images,
    selectedIndex,
    onSelectImage
  }: {
    images: string[];
    selectedIndex: number;
    onSelectImage: (image: string, index: number) => void;
  }) => {
    return (
      <div className="flex space-x-2 overflow-x-auto pb-2">
        {images.map((image, index) => (
          <div
            key={index}
            className={`w-20 h-20 flex-shrink-0 cursor-pointer rounded-xl overflow-hidden border-2 ${selectedIndex === index ? 'border-dzinly-primary' : 'border-transparent'
              }`}
            onClick={() => onSelectImage(image, index)}
          >
            <img
              src={image}
              alt={`Variant ${index + 1}`}
              className="w-full h-full object-cover  "
            />
          </div>
        ))}
      </div>
    );
  };

  const ActionButtons = () => {

    return null;

    return (
      <div className="flex space-x-2">
        <Button variant="outline">
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
          </svg>
          Save
        </Button>
        <Button variant="outline">
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
          </svg>
          Share
        </Button>
        <Button variant="outline">
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          Download
        </Button>
      </div>
    );
  };

  return (
    <div className="flex flex-col lg:flex-row w-full bg-white overflow-y-hidden h-full">
      {/* Left panel with controls */}
      <div className="w-full lg:w-1/3 border-r border-gray-200 flex flex-col">
        {/* Renovate AI Header */}
        <div className="bg-dzinly-primary text-white px-4 py-3 flex items-center justify-between gap-5">
          <a
            href="https://renovateai.app/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center hover:opacity-90 transition-opacity"
            aria-label="Visit Renovate AI"
          >
            <div className="flex items-center">
              <img
                src="/images/renovate-ai-logo-text.png"
                alt="Virtual Staging POWERED BY RENOVATE AI"
                className="h-16 w-auto object-contain"
              />
            </div>
          </a>
          <Button
            className="bg-white text-dzinly-primary hover:bg-gray-100 py-2.5 px-5 rounded-md shadow-sm font-medium text-base whitespace-nowrap flex-shrink-0 min-w-[120px] flex justify-center"
            onClick={handleApplyAndStage}
            disabled={isProcessing || widgetState === "upload"}
          >
            {isProcessing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {processingMessage}
              </>
            ) : (
              <>Generate</>
            )}
          </Button>
        </div>

        {/* Credit display */}
        {creditDetails && (
          <div className="text-center py-2 px-4 border-b">
            <p className="text-sm font-medium">
              {creditDetails.designsLeft}/{creditDetails.totalDesigns} Designs Left
            </p>
            <Button
              className="mt-2 text-xs w-full"
              variant="dzinly"
              onClick={handleBuyMoreCredits}
            >
              Buy More Credits
            </Button>
          </div>
        )}

        <div className="p-4 space-y-6 flex-1 overflow-y-scroll">
          <RoomTypeSelector selectedRoomType={selectedRoomType} onSelectRoomType={setSelectedRoomType} />

          <FurnitureStyleSelector selectedStyle={selectedStyle} onSelectStyle={setSelectedStyle} />

          <div className="flex items-center space-x-2 py-2">
            <Checkbox
              id="auto-declutter"
              checked={useAutoDeclutter}
              onCheckedChange={(checked) => setUseAutoDeclutter(checked as boolean)}
            />
            <div className="flex items-center">
              <label
                htmlFor="auto-declutter"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Fully MLS compliant (No misrepresentation)
              </label>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="ml-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-gray-200 text-gray-600 cursor-help">
                      <Info size={14} />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="max-w-xs">
                    <p>This will not make any structural changes, modify flooring, or otherwise misrepresent the property. Only virtual furniture and decor will be added.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>


        </div>
      </div>

      {/* Right panel with image display and thumbnails */}
      <div className="w-full lg:w-2/3">
        <div className="h-[500px] relative">
          {widgetState === "upload" && <ImageUploader onImageUploaded={handleImageUploaded} />}
          {widgetState === "configure" && <UploadedImagePreview imageUrl={originalImagePreview} />}
          {widgetState === "result" && <ImageComparison beforeImage={originalImagePreview} afterImage={processedImage} />}
        </div>

        {widgetState === "result" && (
          <div className="p-4 border-t border-gray-200">
            <div className="flex justify-between items-center">
              <ThumbnailGallery
                images={generatedImages}
                selectedIndex={selectedImageIndex}
                onSelectImage={handleSelectImage}
              />

            </div>
            <div className="mt-4 flex justify-end">
              <ActionButtons />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}